/**
 * SupportChatWindow
 * -----------------
 * Opens a WebSocket for the selected user session (?sessionId&support&agentId).
 * Renders the conversation and lets the support agent send messages.
 * Closing/leaving the window disconnects the session socket.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

interface ChatMessage {
  from: 'support' | 'user' | 'system';
  text: string;
  timestamp: number;
}

interface Props {
  sessionId: string;
  agentId: number;
  wsUrl: string;
  onLeave: () => void;
}

const SupportChatWindow: React.FC<Props> = ({ sessionId, agentId, wsUrl, onLeave }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [wsStatus, setWsStatus] = useState<'connecting' | 'connected' | 'error' | 'closed'>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const url =
      `${wsUrl}` +
      `?sessionId=${encodeURIComponent(sessionId)}` +
      `&support=true` +
      `&agentId=${encodeURIComponent(agentId)}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus('connected');
      setMessages([
        {
          from: 'system',
          text: `Connected to session ${sessionId}. The user has been notified.`,
          timestamp: Date.now(),
        },
      ]);
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        const from: ChatMessage['from'] =
          payload.from === 'support' ? 'support' : 'user';
        setMessages((prev) => [
          ...prev,
          {
            from,
            text: payload.message,
            timestamp: Date.now(),
          },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          { from: 'user', text: event.data, timestamp: Date.now() },
        ]);
      }
    };

    ws.onerror = () => setWsStatus('error');
    ws.onclose = () => setWsStatus('closed');

    return () => {
      ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(
      JSON.stringify({ type: 'support_message', message: trimmed, agentId })
    );

    setMessages((prev) => [
      ...prev,
      { from: 'support', text: trimmed, timestamp: Date.now() },
    ]);
    setInput('');
  };

  const handleLeave = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'support_message',
          message: '👋 The support agent has left the session.',
          agentId,
        })
      );
    }
    wsRef.current?.close();
    onLeave();
  };

  const statusColour: Record<typeof wsStatus, string> = {
    connecting: '#faad14',
    connected: '#52c41a',
    error: '#f5222d',
    closed: '#888',
  };

  return (
    <Paper
      elevation={2}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: statusColour[wsStatus],
            flexShrink: 0,
          }}
        />
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          Session: {sessionId}
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<ExitToAppIcon />}
          onClick={handleLeave}
          variant="outlined"
        >
          Leave
        </Button>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {wsStatus === 'connecting' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {messages.map((msg, idx) => {
          if (msg.from === 'system') {
            return (
              <Typography key={idx} variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
                {msg.text}
              </Typography>
            );
          }
          const isAgent = msg.from === 'support';
          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: isAgent ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  maxWidth: '70%',
                  bgcolor: isAgent ? 'primary.main' : 'grey.100',
                  color: isAgent ? 'white' : 'text.primary',
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  fontSize: 14,
                }}
              >
                <Typography variant="body2">{msg.text}</Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.7, display: 'block', textAlign: 'right', mt: 0.25 }}
                >
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </Typography>
              </Box>
            </Box>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      <Divider />

      {/* Input */}
      <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          disabled={wsStatus !== 'connected'}
        />
        <Button
          variant="contained"
          size="small"
          endIcon={<SendIcon />}
          onClick={handleSend}
          disabled={wsStatus !== 'connected' || !input.trim()}
          sx={{ minWidth: 80 }}
        >
          Send
        </Button>
      </Box>
    </Paper>
  );
};

export default SupportChatWindow;
